import { useState, useEffect, useRef } from 'react';
import { I18nProvider } from './i18n/context';
import { ThemeProvider } from './theme/context';
import type { Vessel, Survey, SurveyMethod, SurveyType, CalculationResult, Drafts, TankReading } from './types';
import { useVessels } from './hooks/useVessels';
import { useOffline } from './hooks/useOffline';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import VesselSelector from './components/VesselSelector';
import VesselForm from './components/VesselForm';
import DraftInput from './components/DraftInput';
import UllageInput from './components/UllageInput';
import LPGInput from './components/LPGInput';
import ConstantsForm from './components/ConstantsForm';
import ResultsView from './components/ResultsView';
import HelpModal from './components/HelpModal';
import OfflineBanner from './components/OfflineBanner';
import { ToastContainer } from './components/Toast';
import { VesselSkeleton } from './components/Skeleton';
import ErrorBoundary from './components/ErrorBoundary';
import QuickSurvey from './components/QuickSurvey';
import InstallPrompt from './components/InstallPrompt';
import HelpCenter from './components/HelpCenter';
import Settings from './components/Settings';
import * as supa from './lib/supabase';
import * as local from './lib/db';

import { Ship, Mail, Lock, Eye, EyeOff, ArrowLeft, FileText, Anchor, Ruler } from 'lucide-react';

type Step = 'auth' | 'vessels' | 'add-vessel' | 'select-type' | 'method' | 'input' | 'constants' | 'results' | 'quick-survey' | 'help-center' | 'settings';

function AuthScreen() {
  const { signIn, signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isLogin) await signIn(email, password);
      else await signUp(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-ocean-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-ocean-900/40">
            <Ship className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-navy-100">Draft Survey Pro</h1>
          <p className="text-sm text-navy-400 mt-1">Professional cargo calculator</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-3 text-xs text-rose-400 text-center">
              {error}
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-500" />
            <input type="email" className="input-premium pl-10" placeholder="Email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-500" />
            <input type={showPass ? 'text' : 'password'} className="input-premium pl-10 pr-10" placeholder="Password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
            <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-navy-500">
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-sm text-navy-500">
          {isLogin ? "No account?" : "Already have an account?"}{' '}
          <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-ocean-400 hover:text-ocean-300 font-medium">
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, signOut } = useAuth();
  const { vessels, loading, refresh, addVessel, deleteVessel } = useVessels();
  const { isOnline, wasOffline, setWasOffline } = useOffline();
  const [step, setStep] = useState<Step>(user ? 'vessels' : 'auth');
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
  const [surveyType, setSurveyType] = useState<SurveyType>('initial');
  const [method, setMethod] = useState<SurveyMethod>('displacement');
  const [surveyData, setSurveyData] = useState<Partial<Survey>>({});
  const [calcResult, setCalcResult] = useState<CalculationResult | null>(null);
  const [helpId, setHelpId] = useState<string | null>(null);
  const [toasts, setToasts] = useState<{ id: string; type: 'success' | 'error' | 'warning' | 'info'; message: string }[]>([]);

  // Fix: when user logs in, step was stuck at 'auth' from initial render
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    if (user && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      setStep('vessels');
    }
  }, [user]);

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, message, type }]);
  };
  const removeToast = (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const handleVesselSelect = (vessel: Vessel) => {
    setSelectedVessel(vessel);
    setStep('select-type');
  };

  const handleTypeSelect = (type: SurveyType) => {
    setSurveyType(type);
    setSurveyData({
      vessel_id: selectedVessel!.id,
      survey_type: type,
      method: 'displacement',
      constants: selectedVessel!.constants || {},
    });
    setStep('method');
  };

  const handleMethodSelect = (m: SurveyMethod) => {
    setMethod(m);
    setSurveyData((prev) => ({ ...prev, method: m }));
    setStep('input');
  };

  const handleDraftCalc = (result: CalculationResult & { drafts: Drafts }) => {
    setCalcResult(result);
    setSurveyData((prev) => ({
      ...prev,
      drafts: result.drafts,
      corrections: result.corrections,
      displacement: result.displacement,
      density: result.drafts.density,
    }));
    setStep('constants');
  };

  const handleUllageCalc = (result: CalculationResult & { tank_readings: TankReading[] }) => {
    setCalcResult(result);
    setSurveyData((prev) => ({ ...prev, tank_readings: result.tank_readings, total_volume: result.totalGSV }));
    setStep('constants');
  };

  const handleLPGCalc = (result: CalculationResult & { tank_readings: TankReading[] }) => {
    setCalcResult(result);
    setSurveyData((prev) => ({ ...prev, tank_readings: result.tank_readings, cargo_weight: result.totalWeight }));
    setStep('constants');
  };

  const handleConstants = (constants: Record<string, number>) => {
    setSurveyData((prev) => ({ ...prev, constants }));
    setStep('results');
  };

  const handleSave = async () => {
    if (!surveyData.vessel_id) return;
    try {
      const payload: Omit<Survey, 'id' | 'created_at'> = {
        vessel_id: surveyData.vessel_id,
        survey_type: surveyData.survey_type || 'initial',
        survey_number: `DS-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        method: surveyData.method || 'displacement',
        port: surveyData.port,
        berth: surveyData.berth,
        survey_date: new Date().toISOString(),
        density: surveyData.density,
        drafts: surveyData.drafts,
        corrections: surveyData.corrections,
        displacement: surveyData.displacement,
        tank_readings: surveyData.tank_readings,
        total_volume: surveyData.total_volume,
        constants: surveyData.constants || {},
        cargo_weight: surveyData.cargo_weight,
        status: 'completed',
        notes: surveyData.notes,
      };

      let savedSurvey: Survey;
      if (isOnline) {
        savedSurvey = await supa.saveSurvey(payload);
      } else {
        savedSurvey = { ...payload, id: crypto.randomUUID(), created_at: new Date().toISOString() } as Survey;
      }
      await local.saveSurveyLocal(savedSurvey);
      addToast(`${surveyType.toUpperCase()} survey saved successfully`, 'success');

      // If this is final, check if initial exists and offer cargo calc
      if (surveyType === 'final') {
        const allSurveys = await local.getSurveysLocal();
        const initial = allSurveys.find(s => s.vessel_id === surveyData.vessel_id && s.survey_type === 'initial');
        if (initial) {
          addToast('Initial survey found — you can now calculate cargo weight', 'info');
        }
      }
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Save failed', 'error');
    }
  };

  const handleSync = async () => {
    try {
      const localSurveys = await local.getSurveysLocal();
      for (const s of localSurveys) {
        if (!s.id.includes('-')) continue;
        await supa.saveSurvey(s);
      }
      addToast('Sync complete', 'success');
      setWasOffline(false);
      refresh();
    } catch (e) {
      addToast('Sync failed', 'error');
    }
  };

  if (!user) return <AuthScreen />;

  return (
    <Layout isOnline={isOnline} onSignOut={signOut} onHelpCenter={() => setStep('help-center')} onSettings={() => setStep('settings')}>
      <OfflineBanner isOnline={isOnline} wasOffline={wasOffline} onSync={handleSync} />

      {step === 'vessels' && (
        <div className="space-y-6">
          <div className="text-center py-6">
            <h2 className="text-2xl font-bold text-navy-100">Select Vessel</h2>
            <p className="text-sm text-navy-400 mt-1">All vessel data is saved for future surveys</p>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setStep('quick-survey')} className="btn-primary flex-1 flex items-center justify-center gap-2">
              <Ruler className="w-4 h-4" /> Quick Survey
            </button>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => <VesselSkeleton key={i} />)}
            </div>
          ) : (
            <VesselSelector
              vessels={vessels}
              onSelect={handleVesselSelect}
              onAdd={() => setStep('add-vessel')}
              onDelete={deleteVessel}
            />
          )}
        </div>
      )}

      {step === 'add-vessel' && (
        <VesselForm
          onSave={(v) => {
            addVessel(v).then(() => {
              addToast('Vessel added with full data', 'success');
              setStep('vessels');
            }).catch((e) => addToast(e.message, 'error'));
          }}
          onCancel={() => setStep('vessels')}
          onHelpId={setHelpId}
        />
      )}

      {step === 'select-type' && selectedVessel && (
        <div className="space-y-6 animate-fade-in">
          <button type="button" onClick={() => setStep('vessels')} className="text-sm text-navy-400 hover:text-navy-200 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> All Vessels
          </button>

          <div className="glass rounded-xl p-4 flex items-center gap-3">
            <Anchor className="w-5 h-5 text-ocean-400" />
            <div>
              <h2 className="text-xl font-bold text-navy-100">{selectedVessel.name}</h2>
              <p className="text-xs text-navy-400">IMO: {selectedVessel.imo || 'N/A'} | DWT: {selectedVessel.dwt || 'N/A'} MT | LBP: {selectedVessel.lbp || 'N/A'} m</p>
            </div>
          </div>

          <p className="text-sm text-navy-400">Select survey type</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button type="button" onClick={() => handleTypeSelect('initial')} className="card-premium-hover p-6 text-left">
              <div className="w-12 h-12 rounded-xl bg-emerald-600/20 flex items-center justify-center text-2xl mb-3">📥</div>
              <h3 className="font-bold text-navy-100 text-lg">Initial Survey</h3>
              <p className="text-xs text-navy-400 mt-1">Before cargo operations. Records vessel arrival condition.</p>
            </button>
            <button type="button" onClick={() => handleTypeSelect('final')} className="card-premium-hover p-6 text-left">
              <div className="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center text-2xl mb-3">📤</div>
              <h3 className="font-bold text-navy-100 text-lg">Final Survey</h3>
              <p className="text-xs text-navy-400 mt-1">After cargo operations. Used with initial to calculate cargo.</p>
            </button>
          </div>
        </div>
      )}

      {step === 'method' && selectedVessel && (
        <div className="space-y-6 animate-fade-in">
          <button type="button" onClick={() => setStep('select-type')} className="text-sm text-navy-400 hover:text-navy-200 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Change Survey Type
          </button>

          <div className="glass rounded-xl p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 text-ocean-400" />
            <div>
              <h2 className="text-lg font-bold text-navy-100">{selectedVessel.name}</h2>
              <p className="text-xs text-navy-400">{surveyType.toUpperCase()} Survey — Select calculation method</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <button type="button" onClick={() => handleMethodSelect('displacement')} className="card-premium-hover p-5 text-left flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ocean-600/20 flex items-center justify-center text-2xl">📐</div>
              <div>
                <h3 className="font-bold text-navy-100">Displacement Method</h3>
                <p className="text-xs text-navy-400 mt-1">General cargo, bulk, containers. Draft readings + hydrostatic tables.</p>
              </div>
            </button>
            <button type="button" onClick={() => handleMethodSelect('ullage')} className="card-premium-hover p-5 text-left flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-600/20 flex items-center justify-center text-2xl">🛢️</div>
              <div>
                <h3 className="font-bold text-navy-100">Ullage Method</h3>
                <p className="text-xs text-navy-400 mt-1">Tankers. Tank tables + temperature + density correction.</p>
              </div>
            </button>
            <button type="button" onClick={() => handleMethodSelect('lpg')} className="card-premium-hover p-5 text-left flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-600/20 flex items-center justify-center text-2xl">🔥</div>
              <div>
                <h3 className="font-bold text-navy-100">LPG Method</h3>
                <p className="text-xs text-navy-400 mt-1">LPG/LNG carriers. Cryogenic density corrections.</p>
              </div>
            </button>
          </div>
        </div>
      )}

      {step === 'input' && selectedVessel && (
        <div className="space-y-4">
          <button type="button" onClick={() => setStep('method')} className="text-sm text-navy-400 hover:text-navy-200 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Change Method
          </button>
          <div className="glass rounded-xl p-3 flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-bold ${surveyType === 'initial' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>
              {surveyType.toUpperCase()}
            </span>
            <span className="text-sm text-navy-300">{selectedVessel.name} — {method}</span>
          </div>

          {method === 'displacement' && <DraftInput vessel={selectedVessel} onCalculate={handleDraftCalc} setHelpId={setHelpId} />}
          {method === 'ullage' && <UllageInput vessel={selectedVessel} onCalculate={handleUllageCalc} />}
          {method === 'lpg' && <LPGInput vessel={selectedVessel} onCalculate={handleLPGCalc} />}
        </div>
      )}

      {step === 'constants' && <ConstantsForm initial={surveyData.constants || {}} onSubmit={handleConstants} />}

      {step === 'results' && selectedVessel && calcResult && (
        <ResultsView
          vessel={selectedVessel}
          survey={surveyData}
          details={calcResult.details}
          warnings={calcResult.warnings}
          onSave={handleSave}
          onBack={() => setStep('constants')}
        />
      )}

      {step === 'quick-survey' && <QuickSurvey onBack={() => setStep('vessels')} />}
      {step === 'help-center' && <HelpCenter onBack={() => setStep('vessels')} />}
      {step === 'settings' && <Settings onBack={() => setStep('vessels')} />}

      <HelpModal helpId={helpId} isOpen={!!helpId} onClose={() => setHelpId(null)} />
      <InstallPrompt />
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </Layout>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </ThemeProvider>
    </I18nProvider>
  );
}
